import React, { useEffect, useState } from "react";
import Modal from "../Modal";
import Icon from "../Icon";
import { getAuthToken } from "../../utils/authHelpers";
import styles from "./TemplateManager.module.sass";

const apiBase = () =>
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:4000"
    : "";

const TemplateManager = ({ visible, onClose, currentTerms, currentTitle, onUseTemplate }) => {
  const [activeTab, setActiveTab] = useState("browse");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    fetchTemplates();
  }, [visible]);

  const fetchTemplates = async () => {
    setLoading(true);
    const token = getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${apiBase()}/api/collab/templates/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) {
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error("Error loading templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    const token = getAuthToken();

    try {
      const res = await fetch(`${apiBase()}/api/collab/templates/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: saveName.trim(),
          terms: { ...currentTerms, title: currentTitle || undefined },
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setSaveName("");
        setActiveTab("browse");
        fetchTemplates();
      } else {
        alert(data.error || "Failed to save template");
      }
    } catch (err) {
      console.error("Error saving template:", err);
      alert("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    const token = getAuthToken();

    try {
      const res = await fetch(`${apiBase()}/api/collab/templates/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ templateId }),
      });
      const data = await res.json();
      if (data.ok) {
        setTemplates((prev) => prev.filter((t) => t.id !== templateId));
      }
    } catch (err) {
      console.error("Error deleting template:", err);
    }
  };

  const summarizeTerms = (terms) => {
    const parts = [];
    if (terms?.compensation?.type) {
      parts.push(terms.compensation.type.replace(/_/g, " "));
    }
    const delCount = (terms?.deliverables || []).filter((d) => d.title?.trim()).length;
    if (delCount > 0) parts.push(`${delCount} deliverable${delCount > 1 ? "s" : ""}`);
    if (terms?.dates?.type === "specific") parts.push("specific dates");
    if (terms?.dates?.type === "flexible") parts.push("flexible dates");
    return parts.length > 0 ? parts.join(" · ") : "No terms set";
  };

  return (
    <Modal visible={visible} onClose={onClose} outerClassName={styles.outer}>
      <div className={styles.manager}>
        <h2 className={styles.heading}>Templates</h2>
        <p className={styles.subheading}>Save and reuse collaboration term presets.</p>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "browse" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("browse")}
          >
            My Templates
          </button>
          <button
            className={`${styles.tab} ${activeTab === "save" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("save")}
          >
            Save Current
          </button>
        </div>

        {activeTab === "browse" && (
          <>
            {loading ? (
              <div className={styles.loading}>Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className={styles.emptyState}>
                No templates yet. Save your current terms as a template to reuse later.
              </div>
            ) : (
              <div className={styles.templateList}>
                {templates.map((t) => (
                  <div key={t.id} className={styles.templateItem}>
                    <div className={styles.templateInfo}>
                      <p className={styles.templateName}>{t.name}</p>
                      <p className={styles.templateMeta}>{summarizeTerms(t.terms)}</p>
                    </div>
                    <button className={styles.useBtn} onClick={() => onUseTemplate(t)}>
                      Use
                    </button>
                    <button className={styles.deleteTemplateBtn} onClick={() => handleDelete(t.id)}>
                      <Icon name="trash" size="14" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.actions}>
              <button className={styles.cancelBtn} onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}

        {activeTab === "save" && (
          <div className={styles.saveForm}>
            <label className={styles.saveLabel}>Template Name</label>
            <input
              className={styles.saveInput}
              type="text"
              placeholder="e.g. Standard 2-Night Stay"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              autoFocus
            />
            <p className={styles.saveHint}>
              This will save your current draft terms (dates, compensation, deliverables, goals, notes) as a reusable template.
            </p>

            <div className={styles.actions}>
              <button className={styles.cancelBtn} onClick={() => setActiveTab("browse")}>
                Cancel
              </button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={!saveName.trim() || saving}
              >
                {saving ? "Saving..." : "Save Template"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TemplateManager;
